class PredictiveSearch extends HTMLElement {
  constructor() {
    super();
    this.cachedResults = Object.create(null);
    this.input = this.querySelector('input[type="search"]');
    this.predictiveSearchResults = this.querySelector('[data-predictive-search]');
    this.abortController = null;
    this.loadingTimer = null;
    this.predictiveSearchSupported = true;
    this.useStandardSearch = !this.getPredictiveSearchSupport();
    this.resultsQuery = '';
    if (!this.input || !this.predictiveSearchResults) return;
    this.setupEventListeners();
  }
  getPredictiveSearchSupport() {
    const featuresElement = document.getElementById('shopify-features');
    if (!featuresElement) return true;
    try {
      const features = JSON.parse(featuresElement.textContent || '{}');
      return features.predictiveSearch !== false;
    } catch (error) {
      return true;
    }
  }
  disablePredictiveSearch() {
    this.predictiveSearchSupported = false;
    this.abortPendingRequest();
    this.removeAttribute('loading');
    this.removeAttribute('open');
    this.removeAttribute('results');
    if (this.predictiveSearchResults) {
      this.predictiveSearchResults.removeAttribute('style');
    }
    if (this.input) {
      this.input.removeAttribute('role');
      this.input.removeAttribute('aria-expanded');
      this.input.removeAttribute('aria-owns');
      this.input.removeAttribute('aria-controls');
      this.input.removeAttribute('aria-haspopup');
      this.input.removeAttribute('aria-autocomplete');
      this.input.removeAttribute('aria-activedescendant');
    }
  }
  setupEventListeners() {
    const form = this.querySelector('form.search');
    if (form) form.addEventListener('submit', this.onFormSubmit.bind(this));
    this.input.addEventListener('input', debounce((event) => {
      this.onChange(event);
    }, 170).bind(this));
    this.input.addEventListener('focus', this.onFocus.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.addEventListener('keyup', this.onKeyup.bind(this));
    this.addEventListener('keydown', this.onKeydown.bind(this));
  }
  getQuery() {
    return this.input.value.trim();
  }
  onChange() {
    const searchTerm = this.getQuery();
    if (!searchTerm.length) {
      this.close(true);
      return;
    }
    this.getSearchResults(searchTerm);
  }
  onFormSubmit(event) {
    if (!this.getQuery().length || this.querySelector('[aria-selected="true"] a')) event.preventDefault();
  }
  onFocus() {
    const searchTerm = this.getQuery();
    if (!searchTerm.length || !this.predictiveSearchSupported) return;
    if (this.getAttribute('results') === 'true' && this.resultsQuery === searchTerm) {
      this.open();
    } else {
      this.getSearchResults(searchTerm);
    }
  }
  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) this.close();
    });
  }
  onKeyup(event) {
    if (!this.getQuery().length) this.close(true);
    switch (event.code) {
      case 'ArrowUp':
        event.preventDefault();
        this.switchOption('up');
        break;
      case 'ArrowDown':
        event.preventDefault();
        this.switchOption('down');
        break;
      case 'Escape':
        event.preventDefault();
        this.close();
        break;
    }
  }
  onKeydown(event) {
    if (event.code === 'Enter' && this.querySelector('[aria-selected="true"]')) {
      event.preventDefault();
      this.selectOption();
      return;
    }
    if (event.code === 'ArrowUp' || event.code === 'ArrowDown' || (event.code === 'Escape' && this.hasAttribute('open'))) {
      event.preventDefault();
    }
  }
  switchOption(direction) {
    if (!this.getAttribute('open')) return;
    const moveUp = direction === 'up';
    const selectedElement = this.querySelector('[aria-selected="true"]');
    const allElements = this.querySelectorAll('li[role="option"]');
    let activeElement = allElements[0];
    if (!activeElement || (moveUp && !selectedElement)) return;
    this.statusElement = this.statusElement || this.querySelector('.predictive-search-status');
    if (this.statusElement) this.statusElement.textContent = '';
    if (!moveUp && selectedElement) {
      activeElement = selectedElement.nextElementSibling || allElements[0];
    } else if (moveUp) {
      activeElement = selectedElement.previousElementSibling || allElements[allElements.length - 1];
    }
    if (!activeElement || activeElement === selectedElement) return;
    activeElement.setAttribute('aria-selected', true);
    if (selectedElement) selectedElement.setAttribute('aria-selected', false);
    this.setLiveRegionText(activeElement.textContent);
    this.input.setAttribute('aria-activedescendant', activeElement.id);
  }
  selectOption() {
    const selectedProduct = this.querySelector('[aria-selected="true"] a, [aria-selected="true"] button');
    if (selectedProduct) selectedProduct.click();
  }
  getStandardSearchUrl(searchTerm) {
    const form = this.querySelector('form.search');
    const action = form && form.getAttribute('action');
    const root = window.Shopify && window.Shopify.routes && window.Shopify.routes.root || '/';
    const url = new URL(action || `${root}search`, window.location.origin);
    url.searchParams.set('q', searchTerm);
    url.searchParams.set('type', 'product');
    url.searchParams.set('options[prefix]', 'last');
    url.searchParams.set('section_id', 'predictive-search');
    url.searchParams.delete('page');
    return url;
  }

  getSearchRequestUrl(searchTerm) {
    if (this.useStandardSearch) return this.getStandardSearchUrl(searchTerm);
    const root = window.Shopify && window.Shopify.routes && window.Shopify.routes.root || '/';
    const path = window.routes && window.routes.predictive_search_url || `${root}search/suggest`;
    const url = new URL(path, window.location.origin);
    url.searchParams.set('q', searchTerm);
    url.searchParams.set('resources[type]', 'product,collection');
    url.searchParams.set('resources[limit]', '4');
    url.searchParams.set('section_id', 'predictive-search');
    return url;
  }

  async getSearchResults(searchTerm) {
    if (!this.predictiveSearchSupported) return;
    const queryKey = searchTerm.toLowerCase();
    this.abortPendingRequest();
    if (this.cachedResults[queryKey]) {
      this.renderSearchResults(this.cachedResults[queryKey]);
      return;
    }
    this.announceLoadingState();
    this.scheduleVisibleLoadingState();
    const controller = new AbortController();
    this.abortController = controller;
    try {
      let response = await fetch(this.getSearchRequestUrl(searchTerm), {
        signal: controller.signal,
        credentials: 'same-origin'
      });
      if (this.abortController !== controller || searchTerm !== this.getQuery()) return;
      if (response.status === 417 && !this.useStandardSearch) {
        this.useStandardSearch = true;
        this.cachedResults = Object.create(null);
        response = await fetch(this.getStandardSearchUrl(searchTerm), {
          signal: controller.signal,
          credentials: 'same-origin'
        });
      }
      if (this.abortController !== controller || searchTerm !== this.getQuery()) return;
      if (!response.ok) throw new Error(`Search suggestions failed with status ${response.status}`);
      const text = await response.text();
      if (this.abortController !== controller || searchTerm !== this.getQuery()) return;
      const parsedDocument = new DOMParser().parseFromString(text, 'text/html');
      const section = parsedDocument.querySelector('#shopify-section-predictive-search');
      if (!section || !section.querySelector('#predictive-search-results')) {
        throw new Error('Search suggestions section was not returned');
      }
      const resultsMarkup = section.innerHTML;
      this.cachedResults[queryKey] = resultsMarkup;
      this.renderSearchResults(resultsMarkup);
    } catch (error) {
      if (error.name === 'AbortError' || this.abortController !== controller || searchTerm !== this.getQuery()) return;
      this.close();
      this.removeAttribute('results');
      this.resultsQuery = '';
      console.warn('Search suggestions request failed. The search form remains available.', error);
    } finally {
      if (this.abortController === controller) {
        this.abortController = null;
        this.clearLoadingState();
      }
    }
  }

  abortPendingRequest() {
    this.clearLoadingState();
    if (this.abortController) {
      this.abortController.abort();
      this.abortController = null;
    }
  }
  announceLoadingState() {
    this.statusElement = this.statusElement || this.querySelector('.predictive-search-status');
    this.loadingText = this.loadingText || this.getAttribute('data-loading-text');
    if (this.loadingText) this.setLiveRegionText(this.loadingText);
  }
  scheduleVisibleLoadingState() {
    this.loadingTimer = setTimeout(() => {
      this.loadingTimer = null;
      this.setAttribute('loading', true);
      if (this.getAttribute('results') !== 'true') {
        this.predictiveSearchResults.innerHTML = this.getLoadingMarkup();
        this.open();
      }
    }, 350);
  }
  clearLoadingState() {
    if (this.loadingTimer) {
      clearTimeout(this.loadingTimer);
      this.loadingTimer = null;
    }
    this.removeAttribute('loading');
  }
  getLoadingMarkup() {
    return `
      <div class="predictive-search__skeleton" aria-hidden="true">
        ${Array.from({ length: 4 }, () => `
          <div class="predictive-search__skeleton-row">
            <span class="predictive-search__skeleton-image"></span>
            <span class="predictive-search__skeleton-copy">
              <span class="predictive-search__skeleton-line predictive-search__skeleton-line--title"></span>
              <span class="predictive-search__skeleton-line predictive-search__skeleton-line--meta"></span>
            </span>
          </div>
        `).join('')}
      </div>
    `;
  }
  setLiveRegionText(statusText) {
    this.statusElement = this.statusElement || this.querySelector('.predictive-search-status');
    if (!this.statusElement) return;
    this.statusElement.setAttribute('aria-hidden', 'false');
    this.statusElement.textContent = statusText || '';
    setTimeout(() => {
      this.statusElement.setAttribute('aria-hidden', 'true');
    }, 1000);
  }
  renderSearchResults(resultsMarkup) {
    if (!this.predictiveSearchSupported) return;
    this.clearLoadingState();
    this.predictiveSearchResults.innerHTML = resultsMarkup;
    this.setAttribute('results', true);
    this.resultsQuery = this.getQuery();
    this.setLiveRegionResults();
    this.open();
  }
  setLiveRegionResults() {
    this.clearLoadingState();
    const countElement = this.querySelector('[data-predictive-search-live-region-count-value]');
    if (countElement) this.setLiveRegionText(countElement.textContent);
  }
  getResultsMaxHeight() {
    const isMobileHeader = window.matchMedia('(max-width: 749px)').matches
      && this.predictiveSearchResults.classList.contains('predictive-search--header');
    if (isMobileHeader) {
      const inputBottom = this.input.getBoundingClientRect().bottom;
      return Math.max(window.innerHeight - inputBottom - 20, 0);
    }
    const header = document.getElementById('shopify__header__section');
    const headerBottom = header ? header.getBoundingClientRect().bottom : 0;
    this.resultsMaxHeight = Math.max(window.innerHeight - headerBottom, 0);
    return this.resultsMaxHeight;
  }
  open() {
    if (!this.predictiveSearchSupported) return;
    const isMobileHeader = window.matchMedia('(max-width: 749px)').matches
      && this.predictiveSearchResults.classList.contains('predictive-search--header');
    if (isMobileHeader) {
      const inputBottom = Math.round(this.input.getBoundingClientRect().bottom);
      this.predictiveSearchResults.style.setProperty('--predictive-search-mobile-top', `${inputBottom + 8}px`);
    }
    this.predictiveSearchResults.style.maxHeight = `${this.resultsMaxHeight || this.getResultsMaxHeight()}px`;
    this.setAttribute('open', true);
    this.input.setAttribute('aria-expanded', true);
  }
  close(clearSearchTerm = false) {
    this.abortPendingRequest();
    if (clearSearchTerm) {
      this.input.value = '';
      this.removeAttribute('results');
    }
    const selected = this.querySelector('[aria-selected="true"]');
    if (selected) selected.setAttribute('aria-selected', false);
    this.input.setAttribute('aria-activedescendant', '');
    this.removeAttribute('open');
    this.input.setAttribute('aria-expanded', false);
    this.resultsMaxHeight = false;
    this.predictiveSearchResults.removeAttribute('style');
  }
}
if (!customElements.get('predictive-search')) {
  customElements.define('predictive-search', PredictiveSearch);
}
